'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// Modal type definition
export type ModalType = 'messages' | 'tasks' | 'assistant' | 'email' | 'calendar' | 'myshell' | null;

// Back handler type - returns true if it handled the back press, false to continue to next handler
export type ModalBackHandler = () => boolean;
type LegacyBackHandlerRegistrationKey = string;
type RegisterBackHandler = {
  (handler: ModalBackHandler): void;
  (id: LegacyBackHandlerRegistrationKey, handler: ModalBackHandler | (() => void)): void;
};
type UnregisterBackHandler = {
  (handler: ModalBackHandler): void;
  (id: LegacyBackHandlerRegistrationKey): void;
};

// Cache structures for each modal
export interface MessagesCache {
  drafts: Record<string, string>; // conversationId -> draft message
  scrollPosition: number;
  selectedConversationId: string | null;
  unreadCount: number;
}

export interface TasksCache {
  filterStatus: 'all' | 'pending' | 'in-progress' | 'completed';
  filterPriority: 'all' | 'low' | 'medium' | 'high' | 'urgent';
  scrollPosition: number;
  expandedTaskId: string | null;
}

export interface AssistantCache {
  conversationHistory: ChatMessage[];
  inputDraft: string;
  scrollPosition: number;
  context: string | null; // Current page/module context
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// Combined modal state
interface ModalState {
  activeModal: ModalType;
  messagesCache: MessagesCache;
  tasksCache: TasksCache;
  assistantCache: AssistantCache;
}

// Context value type
interface ModalStateContextValue {
  activeModal: ModalType;
  messagesCache: MessagesCache;
  tasksCache: TasksCache;
  assistantCache: AssistantCache;
  
  // Modal control functions
  openModal: (modal: Exclude<ModalType, null>) => void;
  closeModal: () => void;
  toggleModal: (modal: Exclude<ModalType, null>) => void;
  
  // Back button handler registration (for hardware back button on mobile)
  registerBackHandler: RegisterBackHandler;
  unregisterBackHandler: UnregisterBackHandler;
  handleBackPress: () => boolean; // Returns true if a modal handled the back press
  
  // Cache update functions
  updateMessagesCache: (updates: Partial<MessagesCache>) => void;
  updateTasksCache: (updates: Partial<TasksCache>) => void;
  updateAssistantCache: (updates: Partial<AssistantCache>) => void;
  
  // Draft management
  saveDraft: (conversationId: string, draft: string) => void;
  getDraft: (conversationId: string) => string;
  clearDraft: (conversationId: string) => void;
  clearAllDrafts: () => void;
}

// Default cache values
const defaultMessagesCache: MessagesCache = {
  drafts: {},
  scrollPosition: 0,
  selectedConversationId: null,
  unreadCount: 0,
};

const defaultTasksCache: TasksCache = {
  filterStatus: 'all',
  filterPriority: 'all',
  scrollPosition: 0,
  expandedTaskId: null,
};

const defaultAssistantCache: AssistantCache = {
  conversationHistory: [],
  inputDraft: '',
  scrollPosition: 0,
  context: null,
};

// Create context
const ModalStateContext = createContext<ModalStateContextValue | undefined>(undefined);

// LocalStorage keys
const STORAGE_KEYS = {
  MESSAGES_DRAFTS: 'totalaccess_messages_drafts',
  MESSAGES_SCROLL: 'totalaccess_messages_scroll',
  MESSAGES_SELECTED: 'totalaccess_messages_selected',
};

function getInitialMessagesCache(): MessagesCache {
  if (typeof window === 'undefined') {
    return defaultMessagesCache;
  }

  try {
    const savedDrafts = localStorage.getItem(STORAGE_KEYS.MESSAGES_DRAFTS);
    const savedScroll = localStorage.getItem(STORAGE_KEYS.MESSAGES_SCROLL);
    const savedSelected = localStorage.getItem(STORAGE_KEYS.MESSAGES_SELECTED);

    return {
      ...defaultMessagesCache,
      drafts: savedDrafts ? JSON.parse(savedDrafts) : {},
      scrollPosition: savedScroll ? parseInt(savedScroll, 10) : 0,
      selectedConversationId: savedSelected || null,
    };
  } catch (error) {
    console.error('Error loading persisted modal state:', error);
    return defaultMessagesCache;
  }
}

// Provider component
export function ModalStateProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ModalState>(() => ({
    activeModal: null,
    messagesCache: getInitialMessagesCache(),
    tasksCache: defaultTasksCache,
    assistantCache: defaultAssistantCache,
  }));

  // Back handler registry - modals register their back handlers here
  const backHandlersRef = useRef<Set<ModalBackHandler>>(new Set());
  const namedBackHandlersRef = useRef<Map<string, ModalBackHandler>>(new Map());

  // Persist messages data when it changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(STORAGE_KEYS.MESSAGES_DRAFTS, JSON.stringify(state.messagesCache.drafts));
      localStorage.setItem(STORAGE_KEYS.MESSAGES_SCROLL, state.messagesCache.scrollPosition.toString());
      if (state.messagesCache.selectedConversationId) {
        localStorage.setItem(STORAGE_KEYS.MESSAGES_SELECTED, state.messagesCache.selectedConversationId);
      }
    } catch (error) {
      console.error('Error persisting modal state:', error);
    }
  }, [state.messagesCache.drafts, state.messagesCache.scrollPosition, state.messagesCache.selectedConversationId]);

  // Modal control functions
  const openModal = useCallback((modal: Exclude<ModalType, null>) => {
    setState(prev => ({ ...prev, activeModal: modal }));
  }, []);

  const closeModal = useCallback(() => {
    setState(prev => ({ ...prev, activeModal: null }));
  }, []);

  const toggleModal = useCallback((modal: Exclude<ModalType, null>) => {
    setState(prev => ({
      ...prev,
      activeModal: prev.activeModal === modal ? null : modal,
    }));
  }, []);

  // Cache update functions
  const updateMessagesCache = useCallback((updates: Partial<MessagesCache>) => {
    setState(prev => ({
      ...prev,
      messagesCache: { ...prev.messagesCache, ...updates },
    }));
  }, []);

  const updateTasksCache = useCallback((updates: Partial<TasksCache>) => {
    setState(prev => ({
      ...prev,
      tasksCache: { ...prev.tasksCache, ...updates },
    }));
  }, []);

  const updateAssistantCache = useCallback((updates: Partial<AssistantCache>) => {
    setState(prev => ({
      ...prev,
      assistantCache: { ...prev.assistantCache, ...updates },
    }));
  }, []);

  // Draft management functions
  const saveDraft = useCallback((conversationId: string, draft: string) => {
    setState(prev => ({
      ...prev,
      messagesCache: {
        ...prev.messagesCache,
        drafts: {
          ...prev.messagesCache.drafts,
          [conversationId]: draft,
        },
      },
    }));
  }, []);

  const getDraft = useCallback((conversationId: string): string => {
    return state.messagesCache.drafts[conversationId] || '';
  }, [state.messagesCache.drafts]);

  const clearDraft = useCallback((conversationId: string) => {
    setState(prev => ({
      ...prev,
      messagesCache: {
        ...prev.messagesCache,
        drafts: Object.fromEntries(
          Object.entries(prev.messagesCache.drafts).filter(([id]) => id !== conversationId)
        ),
      },
    }));
  }, []);

  const clearAllDrafts = useCallback(() => {
    setState(prev => ({
      ...prev,
      messagesCache: {
        ...prev.messagesCache,
        drafts: {},
      },
    }));
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.MESSAGES_DRAFTS);
    }
  }, []);

  // Back handler registration functions
  const registerBackHandler = useCallback<RegisterBackHandler>((firstArg: string | ModalBackHandler, secondArg?: ModalBackHandler | (() => void)) => {
    if (typeof firstArg === 'string') {
      if (typeof secondArg !== 'function') {
        return;
      }

      const wrappedHandler: ModalBackHandler = () => {
        const result = secondArg();
        return typeof result === 'boolean' ? result : true;
      };

      const existingHandler = namedBackHandlersRef.current.get(firstArg);
      if (existingHandler) {
        backHandlersRef.current.delete(existingHandler);
      }

      namedBackHandlersRef.current.set(firstArg, wrappedHandler);
      backHandlersRef.current.add(wrappedHandler);
      return;
    }

    backHandlersRef.current.add(firstArg);
  }, []);

  const unregisterBackHandler = useCallback<UnregisterBackHandler>((arg: string | ModalBackHandler) => {
    if (typeof arg === 'string') {
      const existingHandler = namedBackHandlersRef.current.get(arg);
      if (existingHandler) {
        backHandlersRef.current.delete(existingHandler);
        namedBackHandlersRef.current.delete(arg);
      }
      return;
    }

    backHandlersRef.current.delete(arg);
  }, []);

  // Called by useCapacitorMobile when hardware back button is pressed
  // Returns true if a modal handled the back press
  const handleBackPress = useCallback((): boolean => {
    // Iterate in reverse insertion order (LIFO) so the most recently registered
    // handler (innermost modal) runs first — matches modal stack behavior.
    const handlers = Array.from(backHandlersRef.current).reverse();
    for (const handler of handlers) {
      const result = handler();
      if (result) {
        return true; // Handler consumed the back press
      }
    }
    return false; // No handler consumed the back press
  }, []);

  const value: ModalStateContextValue = {
    activeModal: state.activeModal,
    messagesCache: state.messagesCache,
    tasksCache: state.tasksCache,
    assistantCache: state.assistantCache,
    openModal,
    closeModal,
    toggleModal,
    registerBackHandler,
    unregisterBackHandler,
    handleBackPress,
    updateMessagesCache,
    updateTasksCache,
    updateAssistantCache,
    saveDraft,
    getDraft,
    clearDraft,
    clearAllDrafts,
  };

  return (
    <ModalStateContext.Provider value={value}>
      {children}
    </ModalStateContext.Provider>
  );
}

// Custom hook to use modal state
export function useModalState() {
  const context = useContext(ModalStateContext);
  if (context === undefined) {
    throw new Error('useModalState must be used within a ModalStateProvider');
  }
  return context;
}

/** Safe version — returns null when outside ModalStateProvider (e.g. in shared hooks) */
export function useModalStateSafe() {
  return useContext(ModalStateContext) ?? null;
}

// Export types
export type { ModalStateContextValue };
